/**
 * Binary Format Store
 *
 * Zustand store for binary format definition management.
 * Handles CRUD operations, editor state, and preview functionality.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { binaryFormatApi } from '@/services/api/binaryFormatApi';
import { useAuthStore } from '@/stores/auth/auth-store';
import type {
  BinaryFormat,
  BinaryField,
  Transform,
  CreateBinaryFormatDTO,
  UpdateBinaryFormatDTO,
  BinaryFormatListFilters,
  TestParseResponse,
  InlineBinaryFormat,
  InputFormat,
  Endianness,
  ValidationMode,
} from '@/types/binaryFormat';
import { createEmptyField, createEmptyFormat, generateFieldId } from '@/types/binaryFormat';

// ================================
// Store State Interface
// ================================

interface BinaryFormatState {
  // Data
  formats: BinaryFormat[];
  systemTemplates: BinaryFormat[];
  selectedFormat: BinaryFormat | null;

  // Editor state
  isEditorOpen: boolean;
  currentFormat: Partial<BinaryFormat> | null;
  editingFormatId: string | null; // null = creating new

  // Preview state
  sampleData: string;
  previewResult: TestParseResponse | null;
  isPreviewLoading: boolean;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  formatsLoaded: boolean;
  error: string | null;

  // Actions - CRUD
  fetchFormats: (filters?: BinaryFormatListFilters) => Promise<void>;
  fetchSystemTemplates: () => Promise<void>;
  getFormat: (formatId: string) => Promise<BinaryFormat | null>;
  createFormat: (data: CreateBinaryFormatDTO) => Promise<BinaryFormat | null>;
  updateFormat: (formatId: string, data: UpdateBinaryFormatDTO) => Promise<BinaryFormat | null>;
  deleteFormat: (formatId: string) => Promise<boolean>;
  publishFormat: (formatId: string) => Promise<BinaryFormat | null>;
  deprecateFormat: (formatId: string) => Promise<BinaryFormat | null>;
  cloneTemplate: (templateId: string, name?: string) => Promise<BinaryFormat | null>;

  // Actions - Editor
  openEditor: (format?: BinaryFormat) => void;
  closeEditor: () => void;
  setCurrentFormat: (format: Partial<BinaryFormat> | null) => void;
  updateCurrentFormat: (updates: Partial<BinaryFormat>) => void;

  // Actions - Field Management
  addField: (field?: Partial<BinaryField>) => void;
  updateField: (fieldId: string, updates: Partial<BinaryField>) => void;
  deleteField: (fieldId: string) => void;
  reorderFields: (fromIndex: number, toIndex: number) => void;

  // Actions - Transform Management
  addTransform: (fieldId: string, transform?: Partial<Transform>) => void;
  updateTransform: (fieldId: string, transformIndex: number, updates: Partial<Transform>) => void;
  deleteTransform: (fieldId: string, transformIndex: number) => void;

  // Actions - Preview
  setSampleData: (data: string) => void;
  runPreview: () => Promise<void>;
  clearPreview: () => void;

  // Actions - Save
  saveCurrentFormat: () => Promise<BinaryFormat | null>;

  // Utils
  setSelectedFormat: (format: BinaryFormat | null) => void;
  setError: (error: string | null) => void;
}

// ================================
// Helper Functions
// ================================

/** Get current user ID from auth store */
const getUserId = (): string => {
  const user = useAuthStore.getState().user;
  if (!user?._id) {
    throw new Error('User not authenticated');
  }
  return user._id;
};

// ================================
// Zustand Store
// ================================

export const useBinaryFormatStore = create<BinaryFormatState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      formats: [],
      systemTemplates: [],
      selectedFormat: null,

      isEditorOpen: false,
      currentFormat: null,
      editingFormatId: null,

      sampleData: '',
      previewResult: null,
      isPreviewLoading: false,

      isLoading: false,
      isSaving: false,
      formatsLoaded: false,
      error: null,

      // ================================
      // CRUD Actions
      // ================================

      fetchFormats: async (filters?: BinaryFormatListFilters) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });

        try {
          const userId = getUserId();
          const result = await binaryFormatApi.listUserFormats(userId, filters);
          set((state) => {
            state.formats = result.items;
            state.isLoading = false;
            state.formatsLoaded = true;
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to fetch formats';
          set((state) => {
            state.error = message;
            state.isLoading = false;
          });
        }
      },

      fetchSystemTemplates: async () => {
        try {
          const templates = await binaryFormatApi.getSystemTemplates();
          set((state) => {
            state.systemTemplates = templates;
          });
        } catch (error) {
          console.error('Failed to fetch system templates:', error);
        }
      },

      getFormat: async (formatId: string) => {
        try {
          const userId = getUserId();
          const format = await binaryFormatApi.getFormat(userId, formatId);
          return format;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to fetch format';
          set((state) => {
            state.error = message;
          });
          return null;
        }
      },

      createFormat: async (data: CreateBinaryFormatDTO) => {
        set((state) => {
          state.isSaving = true;
          state.error = null;
        });

        try {
          const userId = getUserId();
          const format = await binaryFormatApi.createFormat(userId, data);
          set((state) => {
            state.formats.push(format);
            state.isSaving = false;
          });
          return format;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create format';
          set((state) => {
            state.error = message;
            state.isSaving = false;
          });
          return null;
        }
      },

      updateFormat: async (formatId: string, data: UpdateBinaryFormatDTO) => {
        set((state) => {
          state.isSaving = true;
          state.error = null;
        });

        try {
          const userId = getUserId();
          const format = await binaryFormatApi.updateFormat(userId, formatId, data);
          set((state) => {
            const index = state.formats.findIndex((f) => f._id === formatId);
            if (index !== -1) {
              state.formats[index] = format;
            }
            state.isSaving = false;
          });
          return format;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update format';
          set((state) => {
            state.error = message;
            state.isSaving = false;
          });
          return null;
        }
      },

      deleteFormat: async (formatId: string) => {
        try {
          const userId = getUserId();
          await binaryFormatApi.deleteFormat(userId, formatId);
          set((state) => {
            state.formats = state.formats.filter((f) => f._id !== formatId);
          });
          return true;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete format';
          set((state) => {
            state.error = message;
          });
          return false;
        }
      },

      publishFormat: async (formatId: string) => {
        try {
          const userId = getUserId();
          const format = await binaryFormatApi.publishFormat(userId, formatId);
          set((state) => {
            const index = state.formats.findIndex((f) => f._id === formatId);
            if (index !== -1) {
              state.formats[index] = format;
            }
          });
          return format;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to publish format';
          set((state) => {
            state.error = message;
          });
          return null;
        }
      },

      deprecateFormat: async (formatId: string) => {
        try {
          const userId = getUserId();
          const format = await binaryFormatApi.deprecateFormat(userId, formatId);
          set((state) => {
            const index = state.formats.findIndex((f) => f._id === formatId);
            if (index !== -1) {
              state.formats[index] = format;
            }
          });
          return format;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to deprecate format';
          set((state) => {
            state.error = message;
          });
          return null;
        }
      },

      cloneTemplate: async (templateId: string, name?: string) => {
        try {
          const userId = getUserId();
          const format = await binaryFormatApi.cloneTemplate(userId, templateId, name);
          set((state) => {
            state.formats.push(format);
          });
          return format;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to clone template';
          set((state) => {
            state.error = message;
          });
          return null;
        }
      },

      // ================================
      // Editor Actions
      // ================================

      openEditor: (format?: BinaryFormat) => {
        set((state) => {
          state.isEditorOpen = true;
          state.editingFormatId = format?._id || null;
          state.currentFormat = format
            ? { ...format }
            : {
                ...createEmptyFormat(),
                fields: [],
              };
          state.sampleData = format?.samplePayloads?.[0]?.data || '';
          state.previewResult = null;
        });
      },

      closeEditor: () => {
        set((state) => {
          state.isEditorOpen = false;
          state.currentFormat = null;
          state.editingFormatId = null;
          state.sampleData = '';
          state.previewResult = null;
          state.error = null;
        });
      },

      setCurrentFormat: (format: Partial<BinaryFormat> | null) => {
        set((state) => {
          state.currentFormat = format;
        });
      },

      updateCurrentFormat: (updates: Partial<BinaryFormat>) => {
        set((state) => {
          if (state.currentFormat) {
            Object.assign(state.currentFormat, updates);
          }
        });
      },

      // ================================
      // Field Management Actions
      // ================================

      addField: (field?: Partial<BinaryField>) => {
        set((state) => {
          if (state.currentFormat) {
            if (!state.currentFormat.fields) {
              state.currentFormat.fields = [];
            }
            const newField: BinaryField = {
              id: generateFieldId(),
              name: field?.name || '',
              offset: field?.offset ?? state.currentFormat.fields.length,
              type: field?.type || 'uint8',
              ...field,
            };
            state.currentFormat.fields.push(newField);
          }
        });
      },

      updateField: (fieldId: string, updates: Partial<BinaryField>) => {
        set((state) => {
          if (state.currentFormat?.fields) {
            const field = state.currentFormat.fields.find((f) => f.id === fieldId);
            if (field) {
              Object.assign(field, updates);
            }
          }
        });
      },

      deleteField: (fieldId: string) => {
        set((state) => {
          if (state.currentFormat?.fields) {
            state.currentFormat.fields = state.currentFormat.fields.filter(
              (f) => f.id !== fieldId
            );
          }
        });
      },

      reorderFields: (fromIndex: number, toIndex: number) => {
        set((state) => {
          if (state.currentFormat?.fields) {
            const fields = state.currentFormat.fields;
            const [removed] = fields.splice(fromIndex, 1);
            fields.splice(toIndex, 0, removed);
          }
        });
      },

      // ================================
      // Transform Management Actions
      // ================================

      addTransform: (fieldId: string, transform?: Partial<Transform>) => {
        set((state) => {
          if (state.currentFormat?.fields) {
            const field = state.currentFormat.fields.find((f) => f.id === fieldId);
            if (field) {
              if (!field.transforms) {
                field.transforms = [];
              }
              field.transforms.push({
                type: transform?.type || 'scale',
                value: transform?.value ?? 1,
              });
            }
          }
        });
      },

      updateTransform: (fieldId: string, transformIndex: number, updates: Partial<Transform>) => {
        set((state) => {
          if (state.currentFormat?.fields) {
            const field = state.currentFormat.fields.find((f) => f.id === fieldId);
            if (field?.transforms?.[transformIndex]) {
              Object.assign(field.transforms[transformIndex], updates);
            }
          }
        });
      },

      deleteTransform: (fieldId: string, transformIndex: number) => {
        set((state) => {
          if (state.currentFormat?.fields) {
            const field = state.currentFormat.fields.find((f) => f.id === fieldId);
            if (field?.transforms) {
              field.transforms.splice(transformIndex, 1);
            }
          }
        });
      },

      // ================================
      // Preview Actions
      // ================================

      setSampleData: (data: string) => {
        set((state) => {
          state.sampleData = data;
        });
      },

      runPreview: async () => {
        const { currentFormat, sampleData } = get();
        if (!currentFormat || !sampleData) {
          return;
        }

        set((state) => {
          state.isPreviewLoading = true;
          state.error = null;
        });

        try {
          const inlineFormat: InlineBinaryFormat = {
            inputFormat: currentFormat.inputFormat || 'base64',
            defaultEndian: currentFormat.defaultEndian || 'BE',
            expectedBytes: currentFormat.expectedBytes,
            validationMode: currentFormat.validationMode || 'lenient',
            fields: currentFormat.fields || [],
          };

          const result = await binaryFormatApi.previewParse(inlineFormat, sampleData);
          set((state) => {
            state.previewResult = result;
            state.isPreviewLoading = false;
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to preview format';
          set((state) => {
            state.error = message;
            state.isPreviewLoading = false;
          });
        }
      },

      clearPreview: () => {
        set((state) => {
          state.previewResult = null;
        });
      },

      // ================================
      // Save Action
      // ================================

      saveCurrentFormat: async () => {
        const { currentFormat, editingFormatId } = get();
        if (!currentFormat) {
          return null;
        }

        // Validate required fields
        if (!currentFormat.name?.trim()) {
          set((state) => {
            state.error = 'Format name is required';
          });
          return null;
        }

        if (!currentFormat.inputFormat) {
          set((state) => {
            state.error = 'Input format is required';
          });
          return null;
        }

        if (!currentFormat.defaultEndian) {
          set((state) => {
            state.error = 'Default endianness is required';
          });
          return null;
        }

        if (!currentFormat.fields || currentFormat.fields.length === 0) {
          set((state) => {
            state.error = 'At least one field is required';
          });
          return null;
        }

        // Validate field names
        for (const field of currentFormat.fields) {
          if (!field.name?.trim()) {
            set((state) => {
              state.error = `Field at offset ${field.offset} requires a name`;
            });
            return null;
          }
        }

        set((state) => {
          state.isSaving = true;
          state.error = null;
        });

        try {
          const userId = getUserId();
          let format: BinaryFormat;

          if (editingFormatId) {
            // Update existing
            const updateData: UpdateBinaryFormatDTO = {
              name: currentFormat.name,
              description: currentFormat.description,
              tags: currentFormat.tags,
              inputFormat: currentFormat.inputFormat,
              defaultEndian: currentFormat.defaultEndian,
              expectedBytes: currentFormat.expectedBytes,
              validationMode: currentFormat.validationMode,
              fields: currentFormat.fields,
              samplePayloads: currentFormat.samplePayloads,
            };
            format = await binaryFormatApi.updateFormat(userId, editingFormatId, updateData);

            set((state) => {
              const index = state.formats.findIndex((f) => f._id === editingFormatId);
              if (index !== -1) {
                state.formats[index] = format;
              }
            });
          } else {
            // Create new
            const createData: CreateBinaryFormatDTO = {
              name: currentFormat.name!,
              description: currentFormat.description,
              tags: currentFormat.tags,
              inputFormat: currentFormat.inputFormat!,
              defaultEndian: currentFormat.defaultEndian!,
              expectedBytes: currentFormat.expectedBytes,
              validationMode: currentFormat.validationMode,
              fields: currentFormat.fields!,
              samplePayloads: currentFormat.samplePayloads,
            };
            format = await binaryFormatApi.createFormat(userId, createData);

            set((state) => {
              state.formats.push(format);
            });
          }

          set((state) => {
            state.isSaving = false;
            state.isEditorOpen = false;
            state.currentFormat = null;
            state.editingFormatId = null;
          });

          return format;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to save format';
          set((state) => {
            state.error = message;
            state.isSaving = false;
          });
          return null;
        }
      },

      // ================================
      // Utility Actions
      // ================================

      setSelectedFormat: (format: BinaryFormat | null) => {
        set((state) => {
          state.selectedFormat = format;
        });
      },

      setError: (error: string | null) => {
        set((state) => {
          state.error = error;
        });
      },
    })),
    { name: 'binary-format-store' }
  )
);

export default useBinaryFormatStore;
